import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Constellation
import { IconChevronLeftCircle, IconChevronRightCircle } from '@zillow/constellation';

// Components
import Dots from './Dots';

// Hooks
import { useCarousel } from './hooks';

// Styles
import * as S from './styles';

// Types
type CarouselPropTypes = {
  width: number;
  items: Array<React.ReactElement>;
  autoScrollSpeed?: number;
  autoScrollPauseDuration?: number;
};
import type { State } from 'app/shared/types/State';

const TRANSITION_DURATION = 400; // css animation (milliseconds)

const Carousel = ({ width, items = [], autoScrollSpeed, autoScrollPauseDuration }: CarouselPropTypes) => {
  const isMobile: boolean = useSelector((state: State) => state.app.device.isMobile);

  const count = items.length;
  // add items at start and end of carousel to simulate infinite scroll (see ./hooks)
  const extendedItems = [items[items.length - 1]].concat(items).concat(items[0]);

  const [canUseTransition, updateCanUseTransition] = useState(false);
  const {
    canAutoScroll,
    handleAutoScroll,
    handleGoToItem,
    handleNext,
    handlePrev,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    index,
    isInfiniteScrollComplete,
    translateX,
    updateTranslateX,
  } = useCarousel({
    autoScrollPauseDuration,
    autoScrollSpeed,
    count: items.length,
    transitionDuration: TRANSITION_DURATION,
    updateCanUseTransition,
    width,
  });

  // setInterval will reference state & props at time of declaration
  // useRef will use the current value of state/props
  const indexRef = useRef(index);
  const widthRef = useRef(width);
  const canAutoScrollRef = useRef(canAutoScroll);
  indexRef.current = index;
  widthRef.current = width;
  canAutoScrollRef.current = canAutoScroll;

  useEffect(() => {
    if (autoScrollSpeed) {
      const autoScrollInterval = setInterval(() => {
        if (canAutoScrollRef.current) {
          handleAutoScroll({
            index: indexRef.current,
            width: widthRef.current,
          });
        }
      }, autoScrollSpeed);
      return () => clearInterval(autoScrollInterval);
    }
  }, []);

  useEffect(() => {
    updateTranslateX((index + 1) * width);

    setTimeout(() => {
      updateCanUseTransition(true);
    }, TRANSITION_DURATION);
  }, [width]);

  const renderSkeletonCarousel = () => <S.CarouselItem>{items[0]}</S.CarouselItem>;

  const renderCarousel = () => (
    <>
      {extendedItems.map((item, idx) => (
        <S.CarouselItem key={idx}>{item}</S.CarouselItem>
      ))}
    </>
  );

  return (
    <S.Carousel>
      <S.Container
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ transform: `translateX(-${translateX}px)` }}
        useTransition={canUseTransition && isInfiniteScrollComplete}
      >
        {width ? renderCarousel() : renderSkeletonCarousel()}
      </S.Container>
      {!isMobile && (
        <S.Prev onClick={handlePrev}>
          <IconChevronLeftCircle fontColor="brand" size="md" />
        </S.Prev>
      )}
      {!isMobile && (
        <S.Next onClick={handleNext}>
          <IconChevronRightCircle fontColor="brand" size="md" />
        </S.Next>
      )}
      <Dots index={index} count={count} onGoToItem={handleGoToItem} />
    </S.Carousel>
  );
};

export default Carousel;
